import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import { type Sandbox, SandboxSchema, type SandboxTool } from '@/proto/agentplatform/v1/sandbox_pb';
import {
  ListSandboxesResponseSchema,
  CreateSandboxRequestSchema,
  CreateSandboxResponseSchema,
  GetSandboxRequestSchema,
  GetSandboxResponseSchema,
  UpdateSandboxRequestSchema,
  UpdateSandboxResponseSchema,
  DeleteSandboxRequestSchema,
  DeleteSandboxResponseSchema,
  ListSandboxToolsRequestSchema,
  ListSandboxToolsResponseSchema,
  ListSandboxTraceRecordsRequestSchema,
  ListSandboxTraceRecordsResponseSchema,
  type ListSandboxTraceRecordsRequest,
  type SandboxTraceRecord,
} from '@/proto/agentplatform/httpv2/sandbox_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface SandboxListResult {
  data: Sandbox[];
  total: number;
}

export interface SandboxToolListResult {
  data: SandboxTool[];
}

export interface SandboxTraceRecordListResult {
  data: SandboxTraceRecord[];
  total: number;
}

export const sandboxApi = {
  listSandboxes: async (params: ListRequest): Promise<SandboxListResult> => {
    const req = create(ListRequestSchema, {
      page: params.page,
      pageSize: params.pageSize,
      filter: params.filter,
      fuzzy: params.fuzzy,
      orderBy: params.orderBy,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListSandboxes',
      toJson(ListRequestSchema, req)
    );
    const resp = fromJson(ListSandboxesResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  getSandbox: async (id: string): Promise<Sandbox> => {
    const req = create(GetSandboxRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetSandbox',
      toJson(GetSandboxRequestSchema, req)
    );
    const resp = fromJson(GetSandboxResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },

  createSandbox: async (data: Sandbox): Promise<Sandbox> => {
    const req = create(CreateSandboxRequestSchema, { sandbox: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateSandbox',
      toJson(CreateSandboxRequestSchema, req)
    );
    const resp = fromJson(CreateSandboxResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },

  updateSandbox: async (id: string, data: Sandbox): Promise<Sandbox> => {
    const req = create(UpdateSandboxRequestSchema, { id, sandbox: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateSandbox',
      toJson(UpdateSandboxRequestSchema, req)
    );
    const resp = fromJson(UpdateSandboxResponseSchema, responseObject.data);
    return resp.data ?? create(SandboxSchema, {});
  },

  deleteSandbox: async (id: string): Promise<void> => {
    const req = create(DeleteSandboxRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteSandbox',
      toJson(DeleteSandboxRequestSchema, req)
    );
    fromJson(DeleteSandboxResponseSchema, responseObject.data);
  },

  listSandboxTools: async (id: string): Promise<SandboxToolListResult> => {
    const req = create(ListSandboxToolsRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListSandboxTools',
      toJson(ListSandboxToolsRequestSchema, req)
    );
    const resp = fromJson(ListSandboxToolsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [] };
  },

  listSandboxTraceRecords: async (
    params: ListSandboxTraceRecordsRequest
  ): Promise<SandboxTraceRecordListResult> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListSandboxTraceRecords',
      toJson(ListSandboxTraceRecordsRequestSchema, params)
    );
    const resp = fromJson(ListSandboxTraceRecordsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },
};
